import React from "react";
import { Card, Avatar, Spinner, TextInput } from "flowbite-react";
import { ArrowRight, Search } from "lucide-react";
import { Link} from "react-router-dom";
import AppHeader from "./AppHeader";
import { Therapist } from "../types/therapist";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const getInitials = (name: string) => {
  if (!name) return "TT";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const Therapists: React.FC = () => {
  const [therapists, setTherapists] = React.useState<Therapist[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    const fetchTherapists = async () => {
      try {
        // Verificar cache (válido por 5 minutos)
        const cacheKey = 'therapists_cache';
        const cacheTimeKey = 'therapists_cache_time';
        const cachedData = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(cacheTimeKey);
        
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        
        if (cachedData && cacheTime && (now - parseInt(cacheTime)) < fiveMinutes) {
          // Usar cache
          setTherapists(JSON.parse(cachedData));
          setIsLoading(false);
          return;
        }

        // Buscar do servidor
        const getPublicTherapists = httpsCallable<void, { success: boolean; therapists: Therapist[] }>(
          functions,
          "getPublicTherapists"
        );

        const result = await getPublicTherapists();
        
        if (result.data.success) {
          setTherapists(result.data.therapists);
          // Salvar no cache
          localStorage.setItem(cacheKey, JSON.stringify(result.data.therapists));
          localStorage.setItem(cacheTimeKey, now.toString());
        } else {
          setError("Erro ao carregar terapeutas.");
        }
      } catch (err: any) {
        console.error("Erro ao buscar terapeutas:", err);
        setError(err?.message || "Erro ao carregar terapeutas.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTherapists();
  }, []);

  const filteredTherapists = React.useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase();

    if (!normalizedQuery) {
      return therapists;
    }

    return therapists.filter(
      (t) =>
        (t.nomePublico || "").toLowerCase().includes(normalizedQuery) ||
        (t.title || "").toLowerCase().includes(normalizedQuery) ||
        (t.especialidade || "").toLowerCase().includes(normalizedQuery)
    );
  }, [therapists, searchQuery]);

  return (
    <>
      <AppHeader />

      <div className="container bg-[#F3F6F1] mx-auto max-w-6xl px-4 pt-24 pb-8 sm:pt-28 sm:pb-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight text-[#3A5859] sm:text-4xl">
            Nossos Terapeutas
          </h2>
          <p className="mt-3 text-lg text-[#496566]">
            Conheça os profissionais dedicados ao seu bem-estar.
          </p>
        </div>

        <div className="mb-8 max-w-lg mx-auto">
          <TextInput
            id="search"
            className="border-[#A56096]"
            placeholder="Buscar por nome, título ou especialidade..."
            required
            icon={Search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {isLoading && (
          <div className="text-center py-12 flex flex-col items-center justify-center">
            <Spinner aria-label="Carregando terapeutas..." size="xl" />
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        )}
        {error && <p className="text-center text-red-600 py-12">{error}</p>}

        {!isLoading && !error && (
          <>
            {filteredTherapists.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredTherapists.map((t) => (
                  <Card
                    key={t.id}
                    className="flex flex-col bg-white text-lg shadow-md rounded-2xl h-full"
                  >
                    <div className="flex flex-col items-center">
                      <Avatar
                        img={t.profilePhotoURL || undefined}
                        placeholderInitials={getInitials(t.nomePublico)}
                        size="lg"
                        rounded
                      />
                      <h5
                        style={{ color: "#6C4E78" }}
                        className="mt-3 mb-1 text-xl font-bold tracking-tight text-white text-center"
                      >
                        {t.nomePublico}
                      </h5>
                      <span
                        style={{ color: "#4A7C75" }}
                        className="text-sm font-bold font-medium text-white/80"
                      >
                        {t.title}
                      </span>
                    </div>
                    <div
                      style={{ color: "#4A7C75" }}
                      className="flex flex-col flex-grow font-semibold pt-4"
                    >
                      <p className="text-sm text-center line-clamp-4 flex-grow min-h-[80px]">
                        {t.bio}
                      </p>
                      <Link
                        to={`/terapeuta/${t.id}`}
                        className="w-full mt-4 flex items-center justify-center gap-2 bg-[#4A7C75] text-white px-5 py-2 rounded-full font-semibold text-sm hover:bg-[#406b65] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4A7C75] transition-colors"
                      >
                        Ver mais
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <small className="text-sm text-gray-600">
                  Nenhum terapeuta encontrado
                </small>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export { Therapists };
